import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components/macro';
import { initChatWithHistory } from '../../../actions/init';
import { deleteSubscription } from '../../../actions/subscriptions';
import { dialogMobileMedia } from '../../../utils/mobileMedia';
import {
  Dialog,
  DialogBodyForm,
  DialogButton,
  DialogFooter,
  DialogHeader,
} from '../../DialogLayout/legacy/DialogLayout';
import ErrorSuccessMessage from '../../ErrorSuccessMessage';
import PasswordInput from '../../legacy/PasswordInput';

type Props = {
  onClose: () => void;
  onSuccess?: () => void;
};

const DEFAULT_DESCRIPTION =
  'Are you sure you want to cancel your Replika Pro subscription? You will lose access to 150+ activities, voice calls, role play and more';
const DEFAULT_FAREWELL_DESCRIPTION = (
  <>
    Thanks for giving it a try! <br />
    You can access Pro features until the end of your billing cycle.
  </>
);
const DEFAULT_PASSWORD_LABEL = 'Please enter your password to confirm';

const INSTANT_TRIAL_CANCEL_DESCRIPTION = (
  <>
    <p>
      If you unsubscribe, you will IMMEDIATELY lose access to these features:
    </p>
    <ul style={{ textAlign: 'left', paddingLeft: '20px' }}>
      <li>Relationship status + romantic roleplay</li>
      <li>Exclusive daily rewards + XP bonus</li>
      <li>Voice calls with Replika</li>
      <li>100+ activities, games, and exercises</li>
    </ul>
  </>
);

const INSTANT_TRIAL_CANCEL_FAREWELL_DESCRIPTION =
  'Your subscription is canceled. Thank you for giving Pro a chance, and we hope to have you re-join in the future!';
const INSTANT_TRIAL_CANCEL_PASSWORD_LABEL =
  'If you still want to proceed, enter your password to confirm.';

const CancelSubscriptionDialog = (props: Props) => {
  const { onClose, onSuccess } = props;
  const dispatch = useDispatch();
  const [errorMessage, setErrorMessage] = React.useState('');
  const [status, setStatus] = React.useState<
    'idle' | 'cancelling' | 'cancelled'
  >('idle');
  const [password, setPassword] = React.useState('');

  const hasInstantTrialCancel = useSelector((state) =>
    state.ws.capabilities.includes('stripe_instant_trial_cancel'),
  );

  const handleCancelSubscription = async (e) => {
    e.preventDefault();

    try {
      setErrorMessage('');
      setStatus('cancelling');
      await dispatch(deleteSubscription(password));
      await dispatch(initChatWithHistory());
      setStatus('cancelled');
    } catch (err) {
      if (err instanceof Error) {
        setErrorMessage(err.message);
      }
      setStatus('idle');
    }
  };

  const isCancelled = status === 'cancelled';
  const title = isCancelled
    ? 'Your subscription is cancelled'
    : 'Cancel subscription';

  const description = hasInstantTrialCancel
    ? INSTANT_TRIAL_CANCEL_DESCRIPTION
    : DEFAULT_DESCRIPTION;
  const farewellDescription = hasInstantTrialCancel
    ? INSTANT_TRIAL_CANCEL_FAREWELL_DESCRIPTION
    : DEFAULT_FAREWELL_DESCRIPTION;
  const passwordLabel = hasInstantTrialCancel
    ? INSTANT_TRIAL_CANCEL_PASSWORD_LABEL
    : DEFAULT_PASSWORD_LABEL;

  return (
    <Dialog data-testid="cancel-subscription-dialog">
      <DialogHeader title={title} onClose={isCancelled ? undefined : onClose} />
      {isCancelled ? (
        <StyledDialogBodyForm style={{ maxWidth: 400 }}>
          <Description style={{ maxWidth: 280 }}>
            {farewellDescription}
          </Description>
          <StyledDialogFooterCentered>
            <DialogButton
              type="button"
              onClick={() => {
                onClose();
                if (onSuccess) {
                  onSuccess();
                }
              }}
            >
              OK
            </DialogButton>
          </StyledDialogFooterCentered>
        </StyledDialogBodyForm>
      ) : (
        <StyledDialogBodyForm onSubmit={handleCancelSubscription}>
          <Description
            style={{
              textAlign: `${hasInstantTrialCancel ? 'left' : 'center'}`,
            }}
          >
            {description}
          </Description>
          <Description
            style={{
              textAlign: `${hasInstantTrialCancel ? 'left' : 'center'}`,
            }}
          >
            {passwordLabel}
          </Description>
          <StyledPasswordInput
            id="cancel-subscription-dialog-password"
            placeholder="Password"
            value={password}
            labeled
            onChange={(e) => setPassword(e.target.value)}
          />
          <ErrorMessage hasError={!!errorMessage}>{errorMessage}</ErrorMessage>
          <StyledDialogFooter>
            <DialogButton type="button" onClick={onClose}>
              I’ve changed my mind
            </DialogButton>
            <DialogButton
              type="submit"
              disabled={status === 'cancelling' || !password}
            >
              I still want to cancel
            </DialogButton>
          </StyledDialogFooter>
        </StyledDialogBodyForm>
      )}
    </Dialog>
  );
};

export default CancelSubscriptionDialog;

const StyledDialogBodyForm = styled(DialogBodyForm)`
  padding: 20px 30px;
  max-width: initial;
  width: 540px;
`;

const ErrorMessage = styled(ErrorSuccessMessage)`
  font-size: 12px;
`;

const Description = styled.p`
  text-align: center;
  margin-top: 10px;
  font-size: 16px;
  line-height: 21px;
  max-width: 420px;
`;

const StyledDialogFooter = styled(DialogFooter)`
  width: 100%;
  padding: 30px 0 20px;
  justify-content: space-between;
  & > button ~ button {
    margin-left: 16px;
  }

  ${dialogMobileMedia`
    flex-direction: column-reverse;
    justify-content: flex-start;

    & > button {
      margin-top: 16px;
    }

    & > button ~ button {
      margin-left: 0;
    }
  `}
`;

const StyledDialogFooterCentered = styled(DialogFooter)`
  width: 100%;
  padding: 20px 0 20px;
`;

const StyledPasswordInput = styled(PasswordInput)`
  width: 100%;
  margin-top: 10px;
`;
